import { Box, Typography, useTheme } from '@mui/material';
import { Button, Modal, ModalProps, TextInput, Select } from 'components/ui';
import { useProfile } from 'hooks';
import { debounce } from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { UserSummary, useUsersApi } from 'services/api';
import { useMasterApi } from 'services/api/hooks/useMasterApi.hook';
import { SchoolSummary } from 'services/api/models/school';
import log from 'loglevel';
import { FindUserByEmailResponse } from 'services/api/apis/users/dto';
import { Result } from 'services/api/result.type';
import useFeature, { FeatureFlag } from 'hooks/useFeature';
import { RoleMap } from 'hooks/useGetRole';
import { useOrganizationsApi } from 'services/api/hooks/useOrganizationsApi.hook';
import { useSnackbar } from 'notistack';
import { ORGANIZATION_ROLES, OrganizationRole } from 'enum/organization-roles';

export interface AddLeaderModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {
  school?: SchoolSummary | null;
  organizationId: string;
  onSuccess?: () => Promise<void>;
}

export const AddLeaderModal = ({
  school,
  open,
  onClose,
  organizationId,
  onSuccess,
}: AddLeaderModalProps): ReactElement => {
  const theme = useTheme();
  const { profile } = useProfile();
  const organisationLeaderInviteFlowEnabled = useFeature(
    FeatureFlag.OrganisationLeaderInviteFlow,
  );
  const { enqueueSnackbar } = useSnackbar();

  const domainName = school
    ? school.domainName
    : profile?.school?.domainName ?? '';

  const [email, setEmail] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [user, setUser] = useState<UserSummary | null>(null);
  const [error, setError] = useState('');
  const [role, setRole] = useState<OrganizationRole | ''>('');

  const organizationsApi = useOrganizationsApi();

  const handleSave = async () => {
    if (!user) return;
    if (organisationLeaderInviteFlowEnabled && !role) {
      setError('Please select a role.');
      return;
    }
    try {
      const orgId = organizationId;
      const result = await organizationsApi.inviteLeader(
        orgId,
        user.id,
        organisationLeaderInviteFlowEnabled ? role : '',
      );
      if (!result.success) {
        setError(result.error?.message || 'Failed to send invite.');
        return;
      }
      enqueueSnackbar('Invitation sent successfully!', { variant: 'success' });
      setEmail('');
      setUser(null);
      setHasSearched(false);
      setError('');
      setRole('');
      onClose?.();
      if (onSuccess) await onSuccess();
    } catch (e) {
      setError('Failed to send invite.');
    }
  };

  const handleClose = () => {
    setEmail('');
    setHasSearched(false);
    setError('');
    setRole('');
    onClose?.();
  };

  const masterApi = useMasterApi();
  const usersApi = useUsersApi();

  const handleSearchChange = useCallback(
    debounce(async (email: string) => {
      if (!email.endsWith(domainName)) {
        setError(
          `Email address not valid. Please use an email that ends with ${domainName}`,
        );
        return;
      }

      if (error) setError('');

      let result: Result<FindUserByEmailResponse>;

      if (profile?.role.includes(RoleMap.master)) {
        if (!school?.id) {
          log.error('Expected to specify school when adding leader as master');
          return;
        }

        result = await masterApi.findByEmail(organizationId, email);
      } else {
        result = await usersApi.findByEmail(email);
      }

      if (result?.success) {
        const foundUser = result.value;
        if (
          foundUser.role.includes('admin') ||
          foundUser.role.includes('executive-admin') ||
          foundUser.role.includes('master') ||
          foundUser.role.includes('approver')
        ) {
          setError(
            `Cannot invite users with ${foundUser.role} role as organization leaders. Only normal users can be invited.`,
          );
          setUser(null);
          return;
        }

        setUser(foundUser);
      } else {
        setUser(null);
      }

      setHasSearched(true);
    }, 200),
    [usersApi, profile?.primaryEmail, domainName, organizationId],
  );

  useEffect(() => {
    if (!email) {
      setUser(null);
      setError('');
      return;
    }

    void handleSearchChange(email);
  }, [email, handleSearchChange]);

  return (
    <Modal title="Add Organization Leader" open={open} onClose={handleClose}>
      <Box sx={{ width: 500, maxWidth: '80vw' }}>
        <Typography
          sx={{
            fontSize: '0.875em',
            color: theme.palette.neutral.darkest,
            mb: 16,
          }}
        >
          Complete the email address
          {organisationLeaderInviteFlowEnabled ? ' and the role field' : ''}.
        </Typography>
        <Box width="100%" maxWidth={450}>
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 1, md: 0 }}
            flexDirection={{ xs: 'column', sm: 'row' }}
            width="100%"
          >
            <Box flex={1} width={300}>
              <TextInput
                label="Email Address"
                width="100%"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </Box>
            <Box
              pl={8}
              minWidth={150}
              width={{ xs: '100%', sm: 'auto' }}
              mt={{ xs: 4, md: 2 }}
            >
              {hasSearched &&
                (user ? (
                  <Typography>{`${user.firstName} ${user.lastName}`}</Typography>
                ) : (
                  <Typography color="error" fontWeight={600}>
                    User not found
                  </Typography>
                ))}
            </Box>
          </Box>
          {error && (
            <Typography
              style={{
                color: theme.palette.error.main,
                fontWeight: 400,
                fontSize: 14,
              }}
            >
              {error}
            </Typography>
          )}
        </Box>
        {organisationLeaderInviteFlowEnabled && (
          <Box mt={16} width={300}>
            <Select
              label="Organization Role"
              value={role}
              onChange={e => setRole(e.target.value as OrganizationRole)}
              placeholder="Select an organization role"
              required
            >
              {ORGANIZATION_ROLES.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </Box>
        )}
      </Box>

      <Box mt={30} display="flex">
        <Box mr={30}>
          <Button minWidth={100} inverted onClick={handleClose}>
            Cancel
          </Button>
        </Box>
        <Button
          minWidth={100}
          variant="vibrant"
          disabled={!user || (organisationLeaderInviteFlowEnabled && !role)}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </Modal>
  );
};
